import { useAtomValue, useSetAtom } from "jotai";
import { Node, NodeProps } from "@xyflow/react";
import classNames from "classnames";
import { Colors, Icon, Link, Text } from "@zapier/design-system";
import { isRemirrorJSONEmpty } from "@zapier/rich-text-editor";
import { DEFAULT_NODE_COLOR } from "@zapier/canvas-constants";
import {
  DefaultNodeData,
  isPublicAtom,
  togglesAtomResettable,
  isTemplatePageAtom,
  isViewOnlyModeAtom,
  floatingToolbarActiveButtonAtom,
  determineAssetType,
} from "@zapier/canvas-state";

import { getItemsForStepSummary } from "../../../../utils/getItemsForStepSummary";
import { isAssetNode } from "../../../../utils/isAssetNode";
import { isDraftNotTemplateNode } from "../../../../utils/isDraftNotTemplateNode";
import { AssetHeading } from "../../../AssetHeading";
import { truncateUrl } from "../../../../utils/truncateUrl";
import { StepTitle } from "../../../StepTitle";
import { StepSummary } from "../../../StepSummary";
import { TimeSpent } from "../../../TimeMetrics";
import { StepUsage } from "../../../StepUsage";
import { StepStatusRow } from "../../../StepStatusRow";
import { StepDetails } from "../../../StepDetails";
import { FormsSubmissionsCount } from "../../../FormsSubmissionsCount";
import { FlexRow } from "../../../FlexRow";
import { UnstyledButton } from "../../../UnstyledButton";
import { DraftTag } from "../../DraftTag/DraftTag";
import { AssetNodeDeterminer } from "../AssetNodeSkeleton/AssetNodeDeterminer";
import { NotesButton } from "../AssetNodeSkeleton/NotesButton";

import styles from "./DefaultNode.module.css";

interface DefaultNodeContentProps {
  className: string;
  children?: React.ReactNode;
  isViewOnlyMode?: boolean;
  backgroundColor?: string;
}

export function DefaultNodeContentInner({
  className,
  children,
  ...props
}: NodeProps<Node<DefaultNodeData>> & DefaultNodeContentProps) {
  const { data, id, selected, backgroundColor } = props;
  const { label, services, color = DEFAULT_NODE_COLOR, actors, link } = data;
  const isAssetNodeType = isAssetNode(data);
  const toggles = useAtomValue(togglesAtomResettable);
  const isDraftNode = isDraftNotTemplateNode(id);
  const isPublic = useAtomValue(isPublicAtom);
  const isViewOnlyMode = useAtomValue(isViewOnlyModeAtom);
  const setActiveButton = useSetAtom(floatingToolbarActiveButtonAtom);

  const hasNote = !isRemirrorJSONEmpty(data.notesJson);

  const dynamicStyles = {
    "--border-color": color,
    "--background-color": !toggles.processAssetTypeNodes
      ? Colors.BackgroundWeaker
      : backgroundColor,
  };

  const stepSummaryItems = getItemsForStepSummary({
    apps: services,
    actors,
    hasNote: Boolean(hasNote),
  });
  // Currently only supporting details for table nodes and interface nodes
  const expandable =
    (!isPublic &&
      (Boolean(data.tableId) ||
        Boolean(data.projectId) ||
        Boolean(data.pageId))) ||
    Boolean(data.proposalId);

  const canNodeGetAiSuggestion =
    !data.app && !data.projectId && !data.pageId && !data.chatbotId;
  const isTemplatePage = useAtomValue(isTemplatePageAtom);

  const shouldShowDraftState =
    isDraftNode && toggles.aiCanvasComposer && !isTemplatePage;

  const openFloatingLinkInput = () => {
    setActiveButton("link");
  };

  return (
    <div
      className={classNames(
        styles.wrapper,
        {
          [styles.wrapperNew]: toggles.processAssetTypeNodes,
          [styles.assetNode]: isAssetNodeType,
          [styles.draftNode]: shouldShowDraftState,
          "asset-node": isAssetNodeType,
          "default-node": !isAssetNodeType,
        },
        className,
      )}
      style={dynamicStyles as React.CSSProperties}
    >
      {shouldShowDraftState && <DraftTag />}
      {children}
      {!toggles.processAssetTypeNodes && (
        <StepStatusRow
          nodeId={id}
          assetType={determineAssetType(data.services, "default")}
          zapId={undefined}
          interfaceProjectId={data.projectId}
          interfacePageId={data.pageId}
          tableId={data.tableId}
          chatbotId={data.chatbotId}
        />
      )}
      <StepTitle
        nodeId={id}
        label={label}
        isSelected={Boolean(selected)}
        canNodeGetAiSuggestion={canNodeGetAiSuggestion}
        isViewOnlyMode={isViewOnlyMode}
      />
      {!isPublic && !toggles.processAssetTypeNodes && (
        <StepUsage tableId={data.tableId} chatbotId={data.chatbotId} />
      )}
      {!toggles.processAssetTypeNodes && (
        <FlexRow>
          <FormsSubmissionsCount
            nodeId={id}
            interfaceProjectId={data.projectId}
            interfacePageId={data.pageId}
          />
          <TimeSpent timeSpent={data.timeSpent} />
        </FlexRow>
      )}
      {!toggles.processAssetTypeNodes && (
        <AssetHeading
          nodeId={id}
          assetType={determineAssetType(services, "default")}
          tableId={data.tableId}
          interfaceProjectId={data.projectId}
          interfacePageId={data.pageId}
          chatbotId={data.chatbotId}
        />
      )}
      {!toggles.processAssetTypeNodes && stepSummaryItems.length > 0 && (
        <StepSummary
          items={stepSummaryItems}
          isAssetNodeType={isAssetNodeType}
        />
      )}
      {!toggles.processAssetTypeNodes && expandable && (
        <StepDetails
          nodeId={id}
          proposalId={data.proposalId}
          tableId={data.tableId}
          interfacePageId={data.pageId}
          interfaceProjectId={data.projectId}
        />
      )}
      {toggles.processAssetTypeNodes && (
        <div className={styles.flexRow}>
          {link && (
            <div className={styles.linkContainer}>
              <UnstyledButton
                onClick={openFloatingLinkInput}
                disabled={isViewOnlyMode}
              >
                <Text color="TextWeakest" type="MinimalPrint1">
                  {truncateUrl(link, 20)}
                </Text>
              </UnstyledButton>
              <Link href={link} target="_blank" className={styles.offsiteLink}>
                <Icon name="arrowOffsite" size={15} color="GrayWarm6" />
              </Link>
            </div>
          )}
          <NotesButton
            id={id}
            hasNotes={hasNote}
            isViewOnlyMode={isViewOnlyMode}
          />
        </div>
      )}
    </div>
  );
}

/* This is a presentational component - please keep all RF smarts in the outer DefaultNode component
 * We use this to render a node outside of RF.
 * */
export function DefaultNodeContent(
  props: NodeProps<Node<DefaultNodeData>> & DefaultNodeContentProps,
) {
  const { children, ...rest } = props;
  const toggles = useAtomValue(togglesAtomResettable);
  if (toggles.processAssetTypeNodes) {
    return <AssetNodeDeterminer {...rest} />;
  }
  return <DefaultNodeContentInner {...props} />;
}
